/* eslint-disable max-lines */
import { LeftOutlined } from '@ant-design/icons';
import { Col, Row } from 'antd';
import { Notification, Report, RemoteControlIcon } from 'assets/images';
import { BaseButton } from 'components/UI/BaseButton';
import { CleaningReportExportModal } from 'components/UI/CleaningReportExportModal';
import { useError } from 'hooks/useError';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';
import {
  DATA_EXPORT_FORM_FIELDS,
  OPERATION_STATUS
} from 'util/config/constant';
import { getMachineType } from 'util/machine-type';
import { TRANSLATION_NAMESPACES } from 'util/translation/constant';
import * as exportSelector from 'state/global/export/selector';
import { exportCleaningReport } from 'state/global/export/operation';
import { resetDataExportState } from 'state/global/export/action';
import { MEASUREMENT_FORMAT_STORAGE_KEY } from 'state/global/profile/util/constant';
import { selectGlobalMyProfile } from 'state/global/profile/selector';
import { MachineVncCheckOneAlreadyRunModal } from 'components/MachineVnc/MachineVncCheckOneAlreadyRunModal';
import { DataExportedModal } from './components/DataExportedModal';
import MachineCleaningArea from './components/MachineCleaningArea';
import { MachineDeletedModal } from './components/MachineDeletedModal';
import { MachineDeletionModal } from './components/MachineDeletionModal';
import { MachineInformation } from './components/MachineInformation';
import MachineLocation from './components/MachineLocation';
import { MachineStatus } from './components/MachineStatus';
import { RESET_MY_MACHINE_DETAILS_STATE } from './state/action';
import { deleteMachine } from './state/operation';
import * as selector from './state/selector';
import * as operation from '../MachineVnc/state/operation';
import * as machineVncSelector from '../MachineVnc/state/selector';
import { MachineDeletionModalContent } from './components/MachineDeletionModalContent';
import {
  activeOpen,
  showCheckedFirmwareModal,
  showMachineVncCheckOneAlreadyRunModal
} from '../MachineVnc/state/operation';
import { isRemoteControllable } from '../MachineVnc/util/is-remote-controllable';

export const MyMachineDetails = () => {
  const { t } = useTranslation(TRANSLATION_NAMESPACES.MACHINE_DETAILS);
  const globalMyProfile = useSelector((state) => selectGlobalMyProfile(state));
  const errorMessagesJson = t('errorCodes', { returnObjects: true });
  useError('10001', errorMessagesJson);

  const { id } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();

  const { machineDeletionInfo, machineDetailInfo, dataExportInfo } =
    useSelector((state) => ({
      machineDeletionInfo: selector.machineDeletionInfo(state),
      machineDetailInfo: selector.selectMachineDetailsInformationInfo(state),
      dataExportInfo: exportSelector.selectExportInfo(state)
    }));

  const { machineVncSession } = useSelector((state) => ({
    machineVncSession: machineVncSelector.selectMachineVncSession(state)
  }));

  const checkHasCachedVncSession = () => {
    const isCacheEnabled = localStorage.getItem('current-machine-session');

    return isCacheEnabled ? isCacheEnabled === id : false;
  };

  const checkHasVncSessionOpen = () => {
    if (machineVncSession.data && machineVncSession.data.includes(id)) {
      return true;
    }

    return false;
  };

  useEffect(
    () => () => {
      dispatch({
        type: RESET_MY_MACHINE_DETAILS_STATE
      });
      dispatch(resetDataExportState());
    },
    [dispatch]
  );

  // Delete machine + export cleaning reports
  const redirectToNotification = useCallback(() => {
    history.push({
      pathname: '/notifications',
      state: { defaultMachine: machineDetailInfo }
    });
  }, [history, machineDetailInfo]);

  const redirectToCleaningReports = useCallback(() => {
    history.push({
      pathname: '/cleaning-report',
      state: { defaultMachine: machineDetailInfo }
    });
  }, [history, machineDetailInfo]);

  /**
   * @context user click "Access remote control" button
   * @description Handle access VNC session when cliking on "Access remote control"
   * @usage when user proactively click the button to access VNC session
   */
  const handleAccessVncSession = useCallback(() => {
    if (isRemoteControllable(machineDetailInfo.data.softwareVersion || '')) {
      if (!machineVncSession.data) {
        dispatch(activeOpen());
        dispatch(operation.requestMachineVncSession(id));
        localStorage.setItem('current-machine-session', id);
      } else {
        dispatch(showMachineVncCheckOneAlreadyRunModal());
      }
    } else {
      dispatch(showCheckedFirmwareModal());
    }
  }, [
    machineDetailInfo.data.softwareVersion,
    machineVncSession.data,
    dispatch,
    id
  ]);

  const pageTitle = useMemo(() => {
    return t('pageTitle', {
      machineType: getMachineType(machineDetailInfo.data.materialNumber),
      machineAlias: machineDetailInfo.data.alias
    });
  }, [machineDetailInfo.data.alias, machineDetailInfo.data.materialNumber, t]);

  // --- DELETE MACHINE, EXPORT MACHINE'S CLEANING REPORTS
  const [modalVisible, setModalStatus] = useState({
    machineDeletion: false,
    machineDeleted: false,
    dataExported: false,
    cleaningReportExport: false
  });

  const openMachineDeletionModal = useCallback(() => {
    setModalStatus((state) => ({
      ...state,
      machineDeletion: true
    }));
  }, []);

  const closeAllModal = useCallback(() => {
    setModalStatus({
      machineDeletion: false,
      machineDeleted: false,
      dataExported: false,
      dataExport: false
    });
  }, []);

  const onCloseDataExportedModal = useCallback(() => {
    closeAllModal();
    openMachineDeletionModal();
  }, [closeAllModal, openMachineDeletionModal]);

  const onDeleteMachine = useCallback(() => {
    dispatch(deleteMachine(id));
  }, [dispatch, id]);

  const [dataExportFileType, setDataExportFileType] = useState('');

  const onOpenDataExportModal = useCallback(
    (fileType) => {
      closeAllModal();
      setModalStatus((state) => ({
        ...state,
        dataExport: true
      }));
      setDataExportFileType(fileType);
    },
    [closeAllModal]
  );

  const onExportData = useCallback(
    // TODO: add measurement unit
    (values) => {
      dispatch(
        exportCleaningReport({
          ...values,
          measurementFormat: globalMyProfile[MEASUREMENT_FORMAT_STORAGE_KEY],
          filters: {
            machineIds: [id]
          }
        })
      );
    },
    [dispatch, id, globalMyProfile]
  );

  useEffect(() => {
    if (machineDeletionInfo.status === OPERATION_STATUS.SUCCESS) {
      closeAllModal();
      setModalStatus((state) => ({
        ...state,
        machineDeleted: true
      }));
    }
  }, [closeAllModal, machineDeletionInfo.status]);

  useEffect(() => {
    if (dataExportInfo.status === OPERATION_STATUS.SUCCESS) {
      closeAllModal();
      setModalStatus((state) => ({
        ...state,
        dataExported: true
      }));
    }
  }, [closeAllModal, dataExportInfo.status]);

  const onChangeFileTypeForDataExportModal = useCallback(
    (fileType, formInstance) => {
      formInstance.setFieldsValue({
        [DATA_EXPORT_FORM_FIELDS.FILE_TYPE]: fileType
      });
      setDataExportFileType(fileType);
    },
    []
  );

  const renderMachineDeletionContentForExportDataModal = useCallback(
    (formInstance) => (
      <MachineDeletionModalContent
        defaultFileType={dataExportFileType}
        onChangeFileType={(fileType) =>
          onChangeFileTypeForDataExportModal(fileType, formInstance)
        }
        className="mb-4"
      />
    ),
    [dataExportFileType, onChangeFileTypeForDataExportModal]
  );

  const initialExportDataFormValues = useMemo(
    () => ({
      [DATA_EXPORT_FORM_FIELDS.FILE_TYPE]: dataExportFileType,
      [DATA_EXPORT_FORM_FIELDS.COLUMNS]: []
    }),
    [dataExportFileType]
  );

  return (
    <div className="my-machine-details">
      <Row>
        <Link to="/my-machines" className="go-back">
          <LeftOutlined /> &nbsp; {t('goBackButton')}
        </Link>
      </Row>
      <Row className="machine-name">
        <h2>{pageTitle}</h2>
      </Row>
      <Row className="nav-buttons">
        <Col md={18}>
          <BaseButton
            className="base-yellow-btn cleaning-reports-btn"
            type="primary"
            onClick={redirectToCleaningReports}
            icon={<Report />}
            htmlType="submit">
            {t('cleaningReportsButton')}
          </BaseButton>
          <BaseButton
            className="base-yellow-btn notifications-btn"
            type="primary"
            onClick={redirectToNotification}
            icon={<Notification />}
            htmlType="submit">
            {t('notificationsButton')}
          </BaseButton>
          {process.env.REACT_APP_FEATURE_VNC_SESSION_ENABLED === 'true' &&
            !checkHasVncSessionOpen() &&
            !checkHasCachedVncSession() && (
              <BaseButton
                className="base-yellow-btn remote-control-btn"
                type="primary"
                onClick={handleAccessVncSession}
                icon={<RemoteControlIcon />}>
                {t('machineVncPanel.title')}
              </BaseButton>
            )}
        </Col>
        <Col md={6} className="delete-btn-col">
          <BaseButton
            className="delete-machine-btn base-yellow-btn"
            type="primary"
            onClick={openMachineDeletionModal}
            htmlType="submit">
            {t('deleteMachineButton')}
          </BaseButton>
        </Col>
      </Row>
      <Row gutter={[24, { xs: 32, sm: 32, xl: 24 }]}>
        <Col xs={24} sm={24} xl={12}>
          <MachineStatus id={id} />
        </Col>
        <Col xs={24} sm={24} xl={12}>
          <MachineLocation id={id} />
        </Col>
      </Row>
      <Row>
        <Col md={24} lg={24}>
          <MachineCleaningArea id={id} />
        </Col>
      </Row>
      <Row>
        <Col sm={24} xl={12}>
          <MachineInformation id={id} />
        </Col>
      </Row>
      <MachineDeletionModal
        visible={modalVisible.machineDeletion}
        onClose={closeAllModal}
        onDelete={onDeleteMachine}
        loading={machineDeletionInfo.isLoading}
        onOpenDataExportModal={onOpenDataExportModal}
      />
      <CleaningReportExportModal
        title={t('deletionModal.title')}
        visible={modalVisible.dataExport}
        onClose={closeAllModal}
        onFinish={onExportData}
        loading={dataExportInfo.isLoading}
        initialFormValues={initialExportDataFormValues}
        width={700}
        renderFormContent={renderMachineDeletionContentForExportDataModal}
      />
      <MachineDeletedModal visible={modalVisible.machineDeleted} />
      <DataExportedModal
        visible={modalVisible.dataExported}
        onClose={onCloseDataExportedModal}
        onDelete={onDeleteMachine}
        loading={machineDeletionInfo.isLoading}
      />
      {machineVncSession.modal.oneAlreadyRun.visible && (
        <MachineVncCheckOneAlreadyRunModal idParam={id} />
      )}
    </div>
  );
};
